/**
 * Preview for a standalone photo target
 * Displays the actual photo thumbnail using AdaptivePhoto
 *
 * @module pages/tasks/components/previews/PhotoTargetPreview
 */

import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import type { TargetPreviewProps } from '../../types';

export function PhotoTargetPreview({ task }: TargetPreviewProps) {
  // Fetch photo data
  const { data: photoData, isLoading } = usePhotoByIdGraphQL(task.targetId);

  return (
    <div className="flex items-center gap-3 p-2 bg-surface-2/50 rounded border border-border/30">
      <div className="w-12 h-12 bg-surface-2 rounded overflow-hidden flex-shrink-0">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : photoData?.variants ? (
          <AdaptivePhoto
            photo={photoData}
            alt="Task target photo"
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted">Photo</div>
        {photoData?.description && (
          <div className="text-sm text-foreground truncate">{photoData.description}</div>
        )}
      </div>
    </div>
  );
}
